/* The step resolver and the item model — pure functions over the enquiry.

   Source of behaviour: ux-flow.md §4 (the table), "After the signature"
   (items, states), S12 (variants and rows), S14, S15; and the accepted
   prototype (prototype/assets/app.js: claimItems, itemState, openItems,
   undecided, afterSigning/afterStep, screens.S12, s15Render, screens.S0).

   The funnel runs these to navigate without a round trip. The Intake API
   ports them to PHP (wave 2) and serves the same answers from
   `GET …/next-step`, `…/claim`, `…/part` and `…/resume`. Both sides are
   held to contract/fixtures/resolver/*.json. No I/O, no clock, no randomness:
   the same state gives the same answer on both sides. */

import type {
  ArrivalBasis,
  ClaimItem,
  ClaimRow,
  ClaimVariant,
  ClaimView,
  Enquiry,
  Flight,
  FlightSummary,
  ItemKey,
  ItemKind,
  ItemState,
  Leg,
  LegalBranch,
  NextStep,
  Passenger,
  PassengerPart,
  PassengerRef,
  Resume,
  Step,
  Visit,
} from './types';

/* ── Context ─────────────────────────────────────────────── */

export type ResolverScope = { role: 'claimant' } | { role: 'passenger'; passenger: number };

export type ResolverTarget =
  | { kind: 'claim' } /* generic claim link, banner "Open" → S12 */
  | { kind: 'resume' } /* abandonment nudge → just the resolver */
  | { kind: 'item'; itemKey: ItemKey } /* item reminder, rejected upload, S12 row */
  | { kind: 'passenger_part' }; /* a fellow passenger's link → S15 */

export interface ResolveContext {
  /** Who is asking. Default: the claimant. */
  scope?: ResolverScope;
  /** Default `first_pass`. Anything that came back via the banner, an email link or an S12 row is `returning`. */
  visit?: Visit;
  target?: ResolverTarget;
  /** The magic link that brought them was expired or revoked (`POST /auth/exchange` said 410). */
  linkExpired?: boolean;
}

/* ── Small helpers ───────────────────────────────────────── */

/** The minutes of arrival delay from which data alone decides a delay (EC 261 / UK 261: 3 hours). */
export const THREE_HOURS_MIN = 180;

/* 1.4: `landed` (touchdown, no gate time) is an outcome too; its figure is the runway delay. */
const KNOWN_OUTCOMES = new Set(['arrived', 'landed', 'cancelled', 'diverted']);

const stored = (s: Enquiry, key: string): ItemState | undefined => s.items.find(i => i.key === key)?.state;
const storedRecord = (s: Enquiry, key: string) => s.items.find(i => i.key === key);

const ref = (p: Passenger): PassengerRef => ({ n: p.n, firstName: p.firstName });
const passenger = (s: Enquiry, n: number) => s.passengers.find(p => p.n === n);
const adults = (s: Enquiry) => s.passengers.filter(p => !p.isMinor);

const step = (st: Step, extra: Partial<Omit<NextStep, 'step'>> = {}): NextStep => ({
  step: st,
  leg: null,
  itemKey: null,
  passenger: null,
  variant: null,
  ...extra,
});

/* ── Flight facts (S3–S6) ────────────────────────────────── */

/**
 * Which time a flight's `arrivalDelayMin` was measured from (contract 1.4). A 1.3 API sends no
 * `arrivalBasis`: its `arrived` flights were timed at the gate, and nothing else had a figure.
 */
export const arrivalBasisOf = (f: Flight): ArrivalBasis => f.arrivalBasis ?? (f.status === 'arrived' ? 'gate' : 'none');

/** Legs with a flight chosen, listed or typed (not skipped, not unanswered). */
export const pickedLegs = (s: Enquiry): Leg[] => s.legs.filter(l => l.choice === 'listed' || l.choice === 'manual');

/** No usable outcome: typed and not found, or a flight with no arrival/cancellation/diversion on record. */
export const legIsPending = (l: Leg): boolean => !l.flight || !KNOWN_OUTCOMES.has(l.flight.status);

/**
 * The leg S5 shows and the claim is about: the first cancelled or diverted leg, else the first
 * delayed one, else the first pending one, else the last leg picked. Single-leg trips: that leg.
 * (DECISION-NEEDED: multi-leg S5 is not specified in ux-flow.md; this is the contract's reading.)
 */
export function primaryLeg(s: Enquiry): Leg | null {
  const legs = pickedLegs(s);
  if (!legs.length) return null;
  const by = (f: (l: Leg) => boolean) => legs.find(f);
  return (
    by(l => !!l.flight && (l.flight.status === 'cancelled' || l.flight.status === 'diverted')) ??
    by(l => !!l.flight && l.flight.displayStatus === 'delayed') ??
    by(legIsPending) ??
    legs[legs.length - 1]!
  );
}

export type ResultState = 'eligible' | 'below_threshold' | 'pending' | 'not_covered';

/**
 * Which S5 state (prototype `s5State`): cancelled or arrived ≥ 3 h late → eligible; no outcome or
 * diverted → pending (architecture.md §4.2 rule 8: diversions go to self-report); otherwise
 * below threshold. 1.4: a `landed` flight is judged on its runway delay the same way (eligible from
 * 3 h at touchdown, which the gate delay can only exceed; below that, S6 asks the passenger). `not_covered` when the regime is `none` (DECISION-NEEDED, spec gap: S5 has no such state; the
 * funnel shows the below-threshold card without money and carries on — design rule 4).
 */
export function resultState(s: Enquiry): ResultState | null {
  const leg = primaryLeg(s);
  if (!leg) return null;
  if (s.quote?.regime === 'none') return 'not_covered';
  const f = leg.flight;
  if (!f || legIsPending(leg) || f.status === 'diverted') return 'pending';
  if (f.status === 'cancelled') return 'eligible';
  if (f.arrivalDelayMin !== null && f.arrivalDelayMin >= THREE_HOURS_MIN) return 'eligible';
  return 'below_threshold';
}

/** S4 is shown only when the data can't tell us: no status, the passenger disputed S5, or they said "Something else" on S0 (data never shows a denied boarding). */
export const needsSelfReport = (s: Enquiry): boolean =>
  resultState(s) === 'pending' || s.resultResponse === 'disputed' || s.entry === 'other';

/** Which S6 question (prototype `branchOf`, plus the missed-connection rule of ux-flow.md S6). */
export function legalBranch(s: Enquiry): LegalBranch {
  const said = s.selfReport?.whatHappened;
  if (said === 'cancelled' || said === 'denied_boarding' || said === 'missed_connection') return said;
  if (said === 'delayed' || said === 'diverted') return 'delayed';
  const leg = primaryLeg(s);
  const f = leg?.flight;
  if (f?.status === 'cancelled') return 'cancelled';
  /* A delayed flight that wasn't the last one: the question is the arrival at the final destination. */
  if (leg && f?.displayStatus === 'delayed' && leg.index < s.legs.length - 1) return 'missed_connection';
  return 'delayed';
}

/**
 * Why S6 asks the delay question ("How late did you arrive?"), or null when it doesn't (contract 1.4,
 * decisions 145 and 146). Null also when S6's branch isn't a delay (cancelled, denied boarding).
 * - `disputed`: the passenger said our data was wrong on S5 (146): their view, accepted.
 * - `connection`: a missed connection, measured at the final destination: no flight's data says it.
 * - `no_gate_data`: no trusted gate time for the arrival at the final destination: runway only, an
 *   estimate, nothing, or the flight isn't the final one (145).
 * - `gate_under_3h`: a gate time under 3 hours late; S6 decides (the answer counts, 144).
 * - `self_reported`: gate data proves 3 h or more, but they told us what happened on S4 themselves
 *   (S0 "Something else"), so S6 asks as it always did.
 * Gate data that proves 3 hours or more at the final destination, not disputed: null, S6 is skipped.
 */
export type DelayQuestionReason = 'disputed' | 'connection' | 'no_gate_data' | 'gate_under_3h' | 'self_reported';

export function delayQuestion(s: Enquiry): DelayQuestionReason | null {
  const branch = legalBranch(s);
  if (branch !== 'delayed' && branch !== 'missed_connection') return null;
  if (s.resultResponse === 'disputed') return 'disputed';
  if (branch === 'missed_connection') return 'connection';
  const leg = primaryLeg(s);
  const f = leg?.flight;
  if (!leg || !f || leg.index !== s.legs.length - 1) return 'no_gate_data';
  /* DECISION-NEEDED (w8-01): 145 read literally: a runway delay of 3 h or more is asked too (it proves the
     claim, so the verdict doesn't change, but the answer splits 3–4 h from 4 h+ on long haul). Reversible:
     return null here for `runway` with arrivalDelayMin >= THREE_HOURS_MIN. */
  if (arrivalBasisOf(f) !== 'gate' || f.arrivalDelayMin === null) return 'no_gate_data';
  if (f.arrivalDelayMin < THREE_HOURS_MIN) return 'gate_under_3h';
  if (s.selfReport) return 'self_reported';
  return null;
}

/** S6 adds nothing when gate data already proves ≥ 3 h late at the final destination (prototype `skipsS6`; 1.4: gate data only). */
export function skipsLegalQuestion(s: Enquiry): boolean {
  return legalBranch(s) === 'delayed' && delayQuestion(s) === null;
}

const legalAnswered = (s: Enquiry) => !!s.legalAnswer && s.legalAnswer.branch === legalBranch(s);

/* ── Items ───────────────────────────────────────────────── */


/**
 * Every item of the claim, in table order (ux-flow.md "After the signature"), with its effective
 * state (`undecided` = not decided yet). The claimant's view: an adult fellow passenger's
 * signature and ID are one item, `pax:<n>`, present once a link went out or they signed here.
 */
export function claimItems(s: Enquiry): ClaimItem[] {
  const items: ClaimItem[] = [];
  const add = (key: ItemKey, kind: ItemKind, p: Passenger | null, state: ItemState | 'undecided', needed = true) =>
    items.push({ key, kind, passenger: p ? ref(p) : null, needed, state });

  if (!s.bookingReference) add('pnr', 'booking_reference', null, stored(s, 'pnr') ?? 'undecided');
  else if (!s.signedAt) add('sign', 'signature', null, 'undecided');
  add('id', 'id', null, stored(s, 'id') ?? 'undecided');
  for (const p of s.passengers) {
    if (p.isMinor) {
      /* Added after the claimant signed: not on that assignment, so the claimant signs again, for them. */
      if (p.addedAfterSigning && !p.signedAt) add(`signfor:${p.n}`, 'sign_for_minor', p, 'undecided');
      add(`id:${p.n}`, 'id', p, stored(s, `id:${p.n}`) ?? 'undecided');
    } else if (p.handoff === 'here' || p.handoff === 'link') {
      add(`pax:${p.n}`, 'fellow', p, fellowDone(s, p) ? 'provided' : 'waiting');
    }
  }
  add('extra', 'extra', null, stored(s, 'extra') ?? 'undecided', false);
  return items;
}

const fellowDone = (s: Enquiry, p: Passenger) => !!p.signedAt && stored(s, `id:${p.n}`) === 'provided';

/** Needed items not provided yet: the rows under "N things left". */
export const openItems = (s: Enquiry): ClaimItem[] => claimItems(s).filter(it => it.needed && it.state !== 'provided');

/** S13 walks the claimant's own uploads (their ID, minors' IDs) and the optional extra. */
export const isS13Step = (it: ClaimItem): boolean => it.kind === 'id' || it.kind === 'extra';

/** The S13 screens in order ("ALMOST DONE · 1 OF 3" counts these). */
export const s13Steps = (s: Enquiry): ClaimItem[] => claimItems(s).filter(isS13Step);

/** The first S13 item nobody has decided on yet (first pass). */
export const firstUndecided = (s: Enquiry): ClaimItem | null =>
  s13Steps(s).find(it => it.state === 'undecided') ?? null;

/** Adult fellow passengers still waiting for the S11-handoff decision (asked only once the claimant has signed). */
export const handoffUndecided = (s: Enquiry): Passenger[] =>
  s.signedAt ? adults(s).filter(p => p.handoff === null) : [];

/* ── The resolver ────────────────────────────────────────── */

/** ux-flow.md §4: the first thing missing. */
export function nextStep(s: Enquiry, ctx: ResolveContext = {}): NextStep {
  const scope = ctx.scope ?? { role: 'claimant' };
  if (ctx.linkExpired) return step('S14-expired', { passenger: scope.role === 'passenger' ? scope.passenger : null });
  /* A fellow passenger's token always opens their part, whatever state the claim is in. */
  if (scope.role === 'passenger') return step('S15', { passenger: scope.passenger });

  /* Eligibility stage. */
  if (!s.trip.origin || !s.trip.destination) return step('S1');
  if (!s.trip.date) return step('S2');
  const openLeg = s.legs.find(l => l.choice === null);
  if (openLeg || !s.legs.length) return step('S3', { leg: openLeg?.index ?? 0 });
  if (!s.resultResponse) return step('S5');
  if (needsSelfReport(s) && !s.selfReport) return step('S4');
  if (s.legalAnswer?.branch === 'denied_boarding' && !s.legalAnswer.hadDocuments) return step('S6-stop');
  if (!skipsLegalQuestion(s) && !legalAnswered(s)) return step('S6');

  /* Details stage. */
  if (!s.contact) return step('S7');
  if (s.duplicateOf) return step('S8-duplicate');
  const verdict = s.eligibility?.verdict;
  /* not_now: terminal until the re-check flips it. ineligible (out of scope) shows the same
     screen with its reason: nobody is dead-ended (design rule 4). eligible and pending go on. */
  if (verdict === 'not_now' || verdict === 'ineligible') return step('S8');
  if (!s.travellingWith) return step('S9');

  if (!s.signedAt) {
    if (s.bookingReference) return step('S11');
    if (stored(s, 'pnr') === undefined) return step('S10');
    /* Booking reference deferred: signing waits, the items don't. */
    return itemsPhase(s, ctx);
  }

  /* Compensation stage, signed. */
  const fellow = handoffUndecided(s)[0];
  if (fellow) return step('S11-handoff', { passenger: fellow.n });
  return itemsPhase(s, ctx);
}

function itemsPhase(s: Enquiry, ctx: ResolveContext): NextStep {
  const visit = ctx.visit ?? 'first_pass';
  const target = ctx.target;
  if (target?.kind === 'item') {
    const it = claimItems(s).find(i => i.key === target.itemKey);
    const open = it && (it.kind === 'extra' || (it.needed && it.state !== 'provided'));
    if (it && open) {
      const screen = screenForItem(it);
      if (screen) return screen;
    }
    return claimPage(s, 'returning');
  }
  if (target?.kind === 'claim') return claimPage(s, visit);
  if (visit === 'first_pass') {
    const next = firstUndecided(s);
    if (next) return step('S13', { itemKey: next.key });
  }
  return claimPage(s, visit);
}

/** Where an item is done. `pax:<n>` has no screen of the claimant's: its row offers "Ben's here". */
function screenForItem(it: ClaimItem): NextStep | null {
  switch (it.kind) {
    case 'booking_reference':
      return step('S10', { itemKey: it.key });
    case 'signature':
      return step('S11', { itemKey: it.key });
    case 'sign_for_minor':
      return step('S11-sign-for', { itemKey: it.key, passenger: it.passenger!.n });
    case 'id':
    case 'extra':
      return step('S13', { itemKey: it.key });
    case 'fellow':
      return null;
  }
}

const claimPage = (s: Enquiry, visit: Visit) => step('S12', { variant: claimVariant(s, visit) });

/* ── S12 ─────────────────────────────────────────────────── */

/** The S12 variant (prototype `screens.S12.enter`). */
export function claimVariant(s: Enquiry, visit: Visit): ClaimVariant {
  const returning = visit === 'returning';
  const open = openItems(s);
  if (!s.signedAt) return 'not_signed';
  if (!open.length) return returning ? 'all_set' : 'complete';
  if (claimantIdMissing(s)) return returning ? 'returning' : 'signed_id_later';
  return returning ? 'returning' : 'started_fellows_pending';
}

const claimantIdMissing = (s: Enquiry) => openItems(s).some(it => it.passenger === null && it.kind === 'id');

function rowFor(s: Enquiry, it: ClaimItem): ClaimRow {
  const p = it.passenger ? passenger(s, it.passenger.n)! : null;
  const state = it.state as ClaimRow['state'];
  let action: ClaimRow['action'];
  if (it.kind === 'signature' || it.kind === 'sign_for_minor') action = 'sign_now';
  else if (it.kind === 'booking_reference') action = 'add_and_sign';
  else if (it.kind === 'fellow') action = 'hand_over';
  else action = state === 'rejected' ? 'retake' : 'do_it_now';

  const fellow = it.kind === 'fellow' && p;
  return {
    key: it.key,
    kind: it.kind,
    passenger: it.passenger,
    state,
    action,
    openParts: fellow
      ? ([!p.signedAt && 'signature', stored(s, `id:${p.n}`) !== 'provided' && 'id'].filter(Boolean) as ('signature' | 'id')[])
      : null,
    link: fellow ? { via: p.linkVia ?? 'email', email: p.email } : null,
    rejectReason: state === 'rejected' ? (storedRecord(s, it.key)?.rejectReason ?? null) : null,
  };
}

/** Everything S12 renders. */
export function claimView(s: Enquiry, visit: Visit = 'returning'): ClaimView {
  if (!s.claimRef) throw new Error('claimView: the enquiry has no claim reference (no contact yet)');
  const returning = visit === 'returning';
  const open = openItems(s);
  const unsigned = !s.signedAt;
  const idMissing = claimantIdMissing(s);

  /* The claimant's own reminders, said once (prototype `reminders`): fellow passengers' go to them. */
  const withReminders = open
    .filter(it => it.passenger === null || it.kind === 'id')
    .filter(it => it.kind === 'booking_reference' || it.state === 'later' || it.state === 'rejected');

  const others: PassengerRef[] = [];
  for (const it of open) if (it.passenger && !others.some(o => o.n === it.passenger!.n)) others.push(it.passenger);

  return {
    enquiryId: s.id,
    claimRef: s.claimRef,
    variant: claimVariant(s, visit),
    visit,
    revealBoard: !returning && !unsigned,
    airlineName: primaryLeg(s)?.flight?.operator.name ?? null,
    amountPerPassenger: s.eligibility?.amountPerPassenger ?? s.quote?.amountPerPassenger ?? null,
    openItems: open.map(it => rowFor(s, it)),
    remindersNote: withReminders.length && !returning ? { count: withReminders.length } : null,
    waitingOnBookingReference: unsigned ? adults(s).filter(p => p.handoff === null).map(ref) : [],
    leftOut: adults(s).filter(p => p.handoff === 'left_out').map(ref),
    claimantIdMissing: idMissing,
    othersPending: others,
    timelineStart: unsigned
      ? idMissing
        ? 'once_signed_and_id'
        : 'once_signed'
      : idMissing
        ? 'when_id_in'
        : 'this_week',
    whatsappOptIn: s.whatsappOptIn,
    whatsappPhone: s.whatsappOptIn ? s.whatsappPhone : null,
    extraProvided: stored(s, 'extra') === 'provided',
    buddyLink: s.buddyLink,
  };
}

/* ── S15 ─────────────────────────────────────────────────── */

/** `anna@example.com` → `a•••@example.com` (prototype `maskEmail`). */
export function maskEmail(email: string): string {
  const at = email.lastIndexOf('@');
  return `${email[0]}•••@${email.slice(at + 1)}`;
}

/** "VY6650 · Vueling · 8 Sep 2026 · BCN → LHR". */
export function flightSummary(s: Enquiry): FlightSummary {
  const leg = primaryLeg(s);
  return {
    ident: leg?.flight?.operatingIdent ?? leg?.manualIdent ?? null,
    carrierName: leg?.flight?.operator.name ?? null,
    date: leg?.date ?? s.trip.date,
    origin: s.trip.origin?.iata ?? null,
    destination: s.trip.destination?.iata ?? null,
  };
}

/** Everything S15 renders for fellow passenger `n`. `handover`: open on the claimant's phone. */
export function passengerPart(s: Enquiry, n: number, opts: { handover: boolean }): PassengerPart {
  const p = passenger(s, n);
  if (!p || p.isMinor) throw new Error(`passengerPart: no adult fellow passenger ${n}`);
  if (!s.claimRef || !s.contact) throw new Error('passengerPart: the claim has no reference or contact yet');
  const id = stored(s, `id:${n}`) ?? 'undecided';
  const signed = !!p.signedAt;
  return {
    enquiryId: s.id,
    claimRef: s.claimRef,
    passenger: { n, firstName: p.firstName, lastName: p.lastName, emailMasked: p.email ? maskEmail(p.email) : null },
    claimantFirstName: s.contact.firstName,
    flight: flightSummary(s),
    amountPerPassenger: s.eligibility?.amountPerPassenger ?? s.quote?.amountPerPassenger ?? null,
    signature: signed ? 'done' : 'open',
    id,
    idRejectReason: id === 'rejected' ? (storedRecord(s, `id:${n}`)?.rejectReason ?? null) : null,
    active: !signed ? 'sign' : id !== 'provided' ? 'id' : null,
    linkVia: p.linkVia,
    handover: opts.handover,
    buddyLink: p.buddyLink,
    passengerLanguage: p.passengerLanguage ?? s.passengerLanguage,
  };
}

/* ── S14 banner ──────────────────────────────────────────── */

/** A claim rather than a flight check: signed, or the booking reference (and signature) deferred. */
export const isClaim = (s: Enquiry): boolean => !!s.signedAt || (!s.bookingReference && stored(s, 'pnr') !== undefined);

/** The welcome-back banner (prototype `screens.S0.enter`). */
export function resume(s: Enquiry, scope: ResolverScope = { role: 'claimant' }): Resume {
  const base = { enquiryId: s.id, claimRef: s.claimRef, flightDate: s.trip.date };
  if (scope.role === 'passenger') {
    return {
      ...base,
      audience: 'passenger',
      passenger: scope.passenger,
      stage: 'passenger_part',
      thingsLeft: [],
      next: nextStep(s, { scope, visit: 'returning', target: { kind: 'passenger_part' } }),
    };
  }
  if (isClaim(s)) {
    return {
      ...base,
      audience: 'claimant',
      passenger: null,
      stage: 'claim',
      thingsLeft: openItems(s).map(it => rowFor(s, it)),
      next: nextStep(s, { visit: 'returning', target: { kind: 'claim' } }),
    };
  }
  const next = nextStep(s, { visit: 'returning' });
  return {
    ...base,
    audience: 'claimant',
    passenger: null,
    stage: next.step === 'S10' ? 'booking_reference' : 'check',
    thingsLeft: [],
    next,
  };
}
